import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X, Loader2, ShieldCheck } from "lucide-react";
import { API } from "../lib/api";
import { toast } from "sonner";

/**
 * Lead-gated "Download my report (PDF)" button for the /check results page.
 * Captures email + name then triggers a personalised PDF for the user's actual search.
 *
 * The modal is rendered via React Portal directly into <body> so it escapes any
 * parent stacking context (`transform`, `filter`, `perspective`) that would
 * otherwise cause `position: fixed` to be relative to that ancestor and leave
 * the form clipped between adjacent report cards.
 */
export default function CheckReportDownload({
  query,
  industry = "",
  postcode = "",
  buttonClass = "btn-primary",
  buttonLabel = "Download my report (PDF)",
  testid = "check-report-download",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", name: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = () => {
    if (busy) return;
    setOpen(false);
  };

  // Lock body scroll while the modal is open and close on Escape.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && !busy) close(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, busy]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !query) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/check-name/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim() || null,
          query,
          industry: industry || null,
          postcode: postcode || null,
        }),
      });
      if (!res.ok) {
        let msg = "We could not generate your report. Please try again.";
        try { const j = await res.json(); if (j.detail) msg = j.detail; } catch (_) { /* swallow parse error; default msg already set */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (query || "report").replace(/[^A-Za-z0-9]+/g, "-").slice(0, 40);
      a.download = `BNP-Report-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      // GA4 `report_downloaded` - personalised variant tied to a real search.
      // Fires only on a successful download so partial/failed attempts don't
      // pollute the funnel numbers.
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "report_downloaded", {
          report_type: "personalised",
          search_term: query || "",
        });
      }
      toast.success("Your report is downloading. Check your downloads folder.");
      setOpen(false);
      setForm({ email: "", name: "" });
    } catch (err) {
      toast.error(err.message || "Could not download. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const modal = open ? (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-3 sm:p-4 animate-in fade-in overflow-y-auto"
      onClick={close}
      data-testid={`${testid}-modal`}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md p-6 sm:p-7 md:p-8 relative bnp-hero-shadow my-auto max-h-[calc(100vh-1.5rem)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[#9ac7f7]/20 text-[#447694]"
          aria-label="Close"
          data-testid={`${testid}-close`}
        >
          <X size={18} />
        </button>

        <div className="h-12 w-12 rounded-2xl bg-[#71edc9]/30 flex items-center justify-center">
          <ShieldCheck size={22} className="text-[#04273d]" />
        </div>
        <h3 className="font-display text-2xl text-[#04273d] mt-4 font-bold tracking-tight">
          Get your full report
        </h3>
        <p className="text-sm text-[#447694] mt-2 leading-relaxed">
          We&rsquo;ll download your personalised report to your device <strong className="text-[#04273d]">and email a copy</strong> for safekeeping &mdash; covering <span className="font-semibold text-[#04273d]">{query || "your business"}</span> across Companies House and domain availability.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3" data-testid={`${testid}-form`}>
          <div>
            <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">Your email *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="you@yourbusiness.co.uk"
              className="bnp-input"
              data-testid={`${testid}-email`}
            />
          </div>
          <div>
            <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">Your name (optional)</label>
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Sophie Bell"
              className="bnp-input"
              data-testid={`${testid}-name`}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full justify-center mt-2"
            data-testid={`${testid}-submit`}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {busy ? "Preparing your PDF…" : "Email me my report"}
          </button>
          <p className="text-[11px] text-[#447694] text-center leading-relaxed" data-testid={`${testid}-consent`}>
            By submitting, you agree to receive our <span className="font-semibold text-[#04273d]">monthly newsletter and occasional intellectual-property updates</span> from NBR Group. We never sell your email on, and you can unsubscribe with one click.
          </p>
        </form>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={buttonClass}
        data-testid={`${testid}-trigger`}
      >
        <Download size={14} /> {buttonLabel}
      </button>

      {modal && typeof document !== "undefined" && createPortal(modal, document.body)}
    </>
  );
}
